import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import {
  ArchiveBoxXMarkIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { InvoiceStatus } from "@/gql/graphql";
import { PDFIcon } from "@/assets/Icons";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  ArrowTopRightOnSquareIcon,
  CreditCardIcon,
} from "@heroicons/react/20/solid";
import { ConfirmDeleteDialog } from "@/lib/Components/Dialog/ConfirmDeleteDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createInvoiceCheckoutSessionMutation,
  createInvoicePdfMutation,
  deleteInvoiceMutation,
  issueInvoiceMutation,
  resendInvoiceMutation,
} from "@/app/Invoices/GraphQL/invoiceMutations";
import { InvoiceListItem } from "@/app/Invoices/GraphQL/invoiceListQuery";
import { VoidInvoiceDialog } from "@/app/Invoices/Components/Dialogs/VoidInvoiceDialog";
import { InvoicePaymentDialog } from "@/app/Invoices/Components/InvoicePaymentDialog";
import { DollarSignIcon } from "lucide-react";
import { useTranslations } from "use-intl";

export function useInvoiceActions(): ActionsHook<InvoiceListItem> {
  const { mutateAsync } = useGqlMutation(createInvoicePdfMutation);
  const { open: openPdf } = useDialog(PreviewPdfDialog);
  const { mutateAsync: issueInvoice } = useGqlMutation(issueInvoiceMutation);
  const { mutateAsync: deleteInvoice } = useGqlMutation(deleteInvoiceMutation);
  const { mutateAsync: resendInvoice } = useGqlMutation(resendInvoiceMutation);
  const { mutateAsync: createCheckout } = useGqlMutation(
    createInvoiceCheckoutSessionMutation,
  );
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const isOnInvoiceRecord = !!matchRoute({
    to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id",
    fuzzy: true,
  });
  const { open: openDeleteDialog } = useDialog(ConfirmDeleteDialog);
  const { open: openVoidDialog } = useDialog(VoidInvoiceDialog);
  const { open: openPaymentDialog } = useDialog(InvoicePaymentDialog);
  const t = useTranslations("invoice.actions");

  const getSendLabel = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.Draft:
        return t("send");
      case InvoiceStatus.Overdue:
        return t("sendOverdueNotice");
      default:
        return t("resend");
    }
  };

  return (invoice) => {
    return [
      {
        label: t("actions"),
        items: [
          {
            actions: [
              {
                label: t("payOnline"),
                color: "indigo",
                isHidden: ![
                  InvoiceStatus.Issued,
                  InvoiceStatus.Overdue,
                ].includes(invoice.status),
                Icon: CreditCardIcon,
                handleAction: async () => {
                  const res = await createCheckout({
                    id: invoice.id,
                  });

                  window.open(
                    res.createInvoiceCheckoutSession.checkout_session_url,
                    "_blank",
                  );
                },
              },
              {
                label: t("addPayment"),
                isAdminAction: true,
                Icon: DollarSignIcon,
                handleAction: () => {
                  openPaymentDialog({
                    invoiceId: invoice.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: getSendLabel(invoice.status),
                Icon: EnvelopeIcon,
                isAdminAction: true,
                isHidden: invoice.status === InvoiceStatus.Draft,
                handleAction: async () => {
                  await resendInvoice({
                    id: invoice.id,
                  });

                  toast(t("invoiceSent"));
                },
              },
              {
                isAdminAction: true,
                isHidden: invoice.status !== InvoiceStatus.Draft,
                label: t("issue"),
                color: "indigo",
                Icon: PaperAirplaneIcon,
                handleAction: async () => {
                  await issueInvoice({
                    id: invoice.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("view"),
                Icon: ArrowTopRightOnSquareIcon,
                handleAction: () => {
                  const publicUrl = new URL(
                    `/invoices/${invoice.uuid}`,
                    import.meta.env.VITE_APP_URL,
                  ).toString();
                  window.open(publicUrl, "_blank");
                },
              },
              {
                label: t("pdf"),
                Icon: PDFIcon,
                handleAction: async () => {
                  const res = await mutateAsync({
                    id: invoice.id,
                  });
                  openPdf({
                    url: res.createInvoicePdf,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("void"),
                Icon: ArchiveBoxXMarkIcon,
                color: "red",
                isAdminAction: true,
                isHidden: ![
                  InvoiceStatus.Issued,
                  InvoiceStatus.Overdue,
                  InvoiceStatus.Paid,
                ].includes(invoice.status),
                isHiddenTopBar: true,
                handleAction: () => {
                  return openVoidDialog({
                    invoiceId: invoice.id,
                  });
                },
              },
              {
                label: t("delete"),
                Icon: TrashIcon,
                color: "red",
                isAdminAction: true,
                isHidden: invoice.status !== InvoiceStatus.Draft,
                handleAction: async () => {
                  openDeleteDialog({
                    buttonText: t("delete"),
                    message: t("deleteMessage"),
                    title: t("deleteTitle"),
                    onDelete: async () => {
                      await deleteInvoice({
                        id: invoice.id,
                      });

                      if (isOnInvoiceRecord) {
                        await navigate({
                          to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices",
                          params: (prev) => prev,
                        });
                      }
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
